import { useEffect } from "react";

/** Canonical origin of the storefront. The apex (berulia.fr) also serves the
 *  site today, so every canonical is emitted on the www host on purpose. */
export const SITE_URL = "https://www.berulia.fr";
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;
const SUFFIX = "BERULIA";

const upsertMeta = (attr, name, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const JSONLD_ID = "berulia-page-jsonld";

const setJsonLd = (data) => {
  document.getElementById(JSONLD_ID)?.remove();
  if (!data) return;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = JSONLD_ID;
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
};

/** Per-page metadata: title, description, canonical, Open Graph and JSON-LD.
 *
 *  Every route used to serve the same static `<head>` from index.html — same
 *  title and, worse, `canonical: https://www.berulia.fr/` on all 142 URLs,
 *  which told Google the whole catalogue was a duplicate of the homepage.
 *
 *  Tags are updated in place rather than appended (React 19 hoisting would
 *  leave the static ones behind and produce two canonicals).
 *
 *  `path` must start with "/". Pass `jsonLd` as a plain object/array.
 */
export function useSeo({ title, description, path, image, type = "website", jsonLd } = {}) {
  const canonical = path ? `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}` : SITE_URL;
  const fullTitle = title ? (title.includes(SUFFIX) ? title : `${title} — ${SUFFIX}`) : `${SUFFIX} — Prêt-à-porter féminin`;
  const img = image || DEFAULT_IMAGE;
  const serialized = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertLink("canonical", canonical);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
    setJsonLd(serialized ? JSON.parse(serialized) : null);
  }, [fullTitle, description, canonical, type, img, serialized]);
}

/** Shared breadcrumb builder. `trail` = [{name, path}], last item is the
 *  current page and is emitted without `item`, as Google recommends. */
export const breadcrumb = (trail) => ({
  "@type": "BreadcrumbList",
  itemListElement: trail.map((step, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: step.name,
    ...(i < trail.length - 1 ? { item: `${SITE_URL}${step.path}` } : {}),
  })),
});
